<template>
  <!--
    InsCreateEditForm — Calm modal for create-child / create-sibling / edit of an
    insurance catalog node. Level & parent are NEVER asked of the user; they are
    derived from `mode` + `contextNode` (see `targetLevel` / `targetUpperID`).
    dataType is only editable when the target is a ROOT (level 1) — children inherit
    it from the backend, but the PUT payload must still echo it (financial-safety:
    omitting a field on PUT corrupts the row).
  -->
  <calm-modal
    :show="show"
    :title="modalTitle"
    size="md"
    :show-footer="false"
    @close="$emit('close')"
  >
    <form class="ins-form" @submit.prevent="onSubmit">
      <!-- ───── Where it lands (read-only preview) ───── -->
      <div class="ins-form__landing">
        <div class="ins-form__landing__label">
          {{ mode === "edit" ? "ตำแหน่งในแคตตาล็อก" : "จะถูกเพิ่มที่" }}
        </div>
        <nav class="ins-form__crumbs">
          <span
            v-for="(a, i) in landingTrail"
            :key="'lt-' + a.ID"
            class="ins-form__crumb"
          >
            {{ a.Name }}<span v-if="i < landingTrail.length - 1" class="ins-form__sep"> › </span>
          </span>
          <span v-if="mode !== 'edit'" class="ins-form__crumb">
            <span v-if="landingTrail.length" class="ins-form__sep"> › </span>
            <span class="ins-form__new">[ใหม่]</span>
          </span>
          <span class="ins-form__level c-mono">(ระดับ {{ targetLevel }})</span>
        </nav>
      </div>

      <!-- ───── Name (required) ───── -->
      <calm-field label="ชื่อ" required :error="nameError">
        <el-input
          v-model="name"
          size="small"
          maxlength="255"
          placeholder="ชื่อรายการ"
          @input="nameError = ''"
        />
      </calm-field>

      <!-- ───── Detail ───── -->
      <calm-field label="รายละเอียด">
        <el-input
          v-model="detail"
          type="textarea"
          :rows="2"
          size="small"
          placeholder="รายละเอียดเพิ่มเติม (ไม่บังคับ)"
        />
      </calm-field>

      <!-- ───── DataType (roots only) ───── -->
      <calm-field
        v-if="showDataType"
        label="ประเภทข้อมูล"
        hint="กำหนดได้เฉพาะหมวดหลัก (ระดับ 1) — รายการลูกจะสืบทอดอัตโนมัติ"
      >
        <el-select v-model="dataType" size="small" placeholder="เลือกประเภทข้อมูล">
          <el-option
            v-for="dt in dataTypeOptions"
            :key="dt.id"
            :label="dt.label"
            :value="dt.id"
          />
        </el-select>
      </calm-field>

      <!-- ───── Inherited dataType note (child, info only) ───── -->
      <div v-else-if="mode !== 'edit'" class="ins-form__inherit-note">
        ประเภทข้อมูลจะสืบทอดจากหมวดหลักโดยอัตโนมัติ
      </div>

      <!-- ───── Backend error (inline, red) ───── -->
      <div v-if="errorMsg" class="ins-form__error" role="alert">
        {{ errorMsg }}
      </div>

      <!-- ───── Actions ───── -->
      <div class="ins-form__actions">
        <calm-button variant="ghost" size="sm" @click="$emit('close')">
          ยกเลิก
        </calm-button>
        <calm-button variant="primary" size="sm" native-type="submit">
          {{ mode === "edit" ? "บันทึก" : "เพิ่ม" }}
        </calm-button>
      </div>
    </form>
  </calm-modal>
</template>

<script>
import { Input, Select, Option } from "element-ui";
import { INS_DATA_TYPES } from "@/components/lmg/insurance/insDataType.js";

export default {
  name: "InsCreateEditForm",
  components: {
    [Input.name]: Input,
    [Select.name]: Select,
    [Option.name]: Option,
  },
  props: {
    show: { type: Boolean, default: false },
    mode: {
      type: String,
      default: "create-child",
      validator: (v) =>
        ["create-child", "create-sibling", "edit"].indexOf(v) !== -1,
    },
    // For create: the parent (child) or sibling-of (sibling). For edit: the node itself.
    contextNode: { type: Object, default: null },
    // node[] root → parent, for the landing-preview breadcrumb.
    ancestors: { type: Array, default: () => [] },
    // backend error message (already localized by the parent), shown inline.
    errorMsg: { type: String, default: "" },
  },
  data() {
    return {
      name: "",
      detail: "",
      dataType: 1,
      nameError: "",
    };
  },
  computed: {
    // ── derived target (NEVER user-chosen) ──
    targetUpperID() {
      if (!this.contextNode) return 0;
      if (this.mode === "create-child") return this.contextNode.ID;
      if (this.mode === "create-sibling") return this.contextNode.UpperID;
      // edit
      return this.contextNode.UpperID;
    },
    targetLevel() {
      if (!this.contextNode) return 1;
      if (this.mode === "create-child") return this.contextNode.Level + 1;
      if (this.mode === "create-sibling") return this.contextNode.Level;
      // edit
      return this.contextNode.Level;
    },
    // dataType control shown only when the target is a root (level 1),
    // i.e. creating a root OR editing an existing root.
    showDataType() {
      if (this.mode === "edit") {
        return !!this.contextNode && this.contextNode.Level === 1;
      }
      return this.targetLevel === 1;
    },
    dataTypeOptions() {
      return INS_DATA_TYPES.map((dt) => ({
        id: dt.id,
        label: this.dataTypeLabel(dt.key, dt.id),
      }));
    },
    landingTrail() {
      // For edit: ancestors are root→parent; the node itself is shown via title.
      // For create: ancestors already include the parent (caller appends it).
      return Array.isArray(this.ancestors) ? this.ancestors : [];
    },
    modalTitle() {
      if (this.mode === "edit") return "แก้ไขรายการ";
      if (this.mode === "create-sibling") return "เพิ่มพี่น้อง";
      return "เพิ่มรายการลูก";
    },
  },
  watch: {
    // Seed fields when the modal opens (show: false → true).
    show(opened) {
      if (opened) this.seed();
    },
  },
  methods: {
    dataTypeLabel(key, id) {
      const label = this.$t(key);
      return label === key ? String(id) : label;
    },
    seed() {
      this.nameError = "";
      if (this.mode === "edit" && this.contextNode) {
        this.name = this.contextNode.Name || "";
        this.detail = this.contextNode.Detail || "";
        this.dataType = this.contextNode.DataType || 1;
      } else {
        this.name = "";
        this.detail = "";
        this.dataType = 1;
      }
    },
    onSubmit() {
      const trimmed = (this.name || "").trim();
      if (!trimmed) {
        this.nameError = "กรุณากรอกชื่อ";
        return;
      }
      let payload;
      if (this.mode === "edit") {
        // PUT needs ALL fields — echo dataType (root: picked / child: contextNode's)
        // and isActive so the row is never corrupted.
        const editableRoot =
          !!this.contextNode && this.contextNode.Level === 1;
        payload = {
          mode: "edit",
          id: this.contextNode.ID,
          name: trimmed,
          detail: (this.detail || "").trim(),
          dataType: editableRoot
            ? this.dataType
            : this.contextNode.DataType,
          isActive: this.contextNode.IsActive,
        };
      } else {
        payload = {
          mode: "create",
          name: trimmed,
          detail: (this.detail || "").trim(),
          level: this.targetLevel,
          upperID: this.targetUpperID,
          // dataType is only chosen at level 1 (roots). For children the backend
          // RE-DERIVES it from the parent line, but still validates the sent
          // value is 1-5 first — so echo the parent's DataType (never 0).
          dataType:
            this.targetLevel === 1
              ? this.dataType
              : this.contextNode.DataType || 1,
        };
      }
      this.$emit("submit", payload);
    },
  },
};
</script>

<style scoped>
.ins-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.ins-form__landing {
  background: var(--c-surface-2, #f6f8fb);
  border: 1px solid var(--c-border, #e4e9f1);
  border-radius: 8px;
  padding: 10px 12px;
}
.ins-form__landing__label {
  font-size: 11px;
  letter-spacing: 0.02em;
  color: #8a96a3;
  margin-bottom: 4px;
}
.ins-form__crumbs {
  font-size: 13px;
  color: var(--c-text, #2c3e50);
  line-height: 1.6;
}
.ins-form__sep {
  color: #b3bdc9;
}
.ins-form__new {
  color: var(--c-primary, #3b6ea8);
  font-weight: 600;
}
.ins-form__level {
  margin-left: 8px;
  font-size: 12px;
  color: #8a96a3;
}

.ins-form__inherit-note {
  font-size: 12px;
  color: #8a96a3;
  margin-top: -4px;
}

.ins-form__error {
  background: #fdecea;
  border: 1px solid #f5c6cb;
  color: #c0392b;
  font-size: 13px;
  border-radius: 8px;
  padding: 8px 12px;
}

.ins-form__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 4px;
}

/* keep el-select full-width inside the calm field */
.ins-form >>> .el-select {
  width: 100%;
}
</style>
